/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { useEffect, useState } from "react";
import AutocompleteSelect from "../Shared/Dropdown/AutocompleteSelect";
import { auditReportTypeValues, constStr, customTasks, repeatOptions } from "../../globals/constants";
import { useTaskDisplayData } from "./hooks";
import { useStoreDispatch } from "../../store/hooks";
import { useSelector } from "react-redux";
import CustomDateTimePicker from "../Shared/DateTimePicker/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { RadixSelectControl } from "../ui/radix-select";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { setScheduledTask } from "../../store/api/tasks/reducer";
import { isEditTaskSelector, tasksSelector } from "../../store/api/tasks/selector";
import { getNewDeviceList } from "./helpers";
import { errorMsgSelector } from "../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../store/helpers/helperReducers";
import { DateTimeValidationError } from "@mui/x-date-pickers";
import "../../pages/DeviceDetail/DeviceDetails.css";
import { Link, useNavigate } from "react-router-dom";
import { setSelectedDeviceSiteGroup } from "../../store/filters/Nodes/reducer";
import { currentSiteGroupsSelector, selectedGeoRegionIdsSelector, selectedGeoRegionsSelector, selectedTaskTypeSelector } from "../../store/api/admin/selectors";
import { getAllSiteGroupList } from "../helpers";
import { setSelectedTaskType } from "../../store/api/admin/reducers";
import { AppState } from "../../store";
import { IGeoRegion } from "../../store/api/admin/types";
import { IServiceDeviceData } from "../../store/api/devices/types";
import { IOption } from "../Shared/Filter/types";
import { useRegionDisplay } from "../../pages/Administrations/AccessControls/Regions/hooks";

dayjs.extend(utc);

export const TaskListView = () => {
  const dispatch = useStoreDispatch();
  const taskList = Object.entries(customTasks).map(([key, value]) => ({ title: value, value: key }));
  const scheduledtasks = useSelector(tasksSelector);
  const isEditTask = useSelector(isEditTaskSelector);

  const autocompleteSelectHandler = (event: any, newValue: any | null) => {
    dispatch(setScheduledTask({ taskName: newValue || { title: "", value: "" } }));
  };

  useEffect(() => {
    if (isEditTask) {
      if (scheduledtasks?.executionTarget) {
        dispatch(setSelectedTaskType(scheduledtasks?.executionTarget?.toUpperCase() || "DEVICE"));
      }
      else {
        dispatch(setSelectedTaskType("DEVICE"));
      }
    }
  }, [dispatch, isEditTask, scheduledtasks?.executionTarget])

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-2">
        <Label>Search Task</Label>
        <AutocompleteSelect
          disabled={isEditTask}
          id={"selectTask"}
          name="taskName"
          options={taskList}
          label={""}
          placeholder="Search task..."
          margin={"normal"}
          size={"small"}
          value={scheduledtasks?.taskName}
          onChange={autocompleteSelectHandler}
          disablePortal
        />
      </div>
    </div>
  );
};

export const DeviceList = () => {
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const { deviceList, deviceRowData } = useTaskDisplayData();
  const scheduledtasks = useSelector(tasksSelector);
  const currentSiteGroups = useSelector(currentSiteGroupsSelector);
  const selectionType = useSelector(selectedTaskTypeSelector);
  const selectedGeoRegions = useSelector<AppState, IGeoRegion[]>(selectedGeoRegionsSelector) || [];
  const selectedGeoRegionIds = useSelector<AppState, string[] | undefined>(selectedGeoRegionIdsSelector);
  const { geoRegions } = useRegionDisplay();
  const [selectedGroupRegionKey, setSelectedGroupRegionKey] = useState<string>("");
  const [selectedDeviceRegionName, setSelectedDeviceRegionName] = useState<string>("");

  // Derive Region options for the GROUP selection: only regions that
  // have at least one site group and are currently selected globally.
  const updatedGeoRegions: IGeoRegion[] = geoRegions || [];
  const allowedRegionIds = (selectedGeoRegionIds && selectedGeoRegionIds.length > 0)
    ? selectedGeoRegionIds
    : updatedGeoRegions.map((region: IGeoRegion) => region.objectKey);

  const groupRegionOptions = updatedGeoRegions
    .filter((region: IGeoRegion) =>
      allowedRegionIds.includes(region.objectKey) &&
      (currentSiteGroups || []).some((group: any) => group.parentKey === region.objectKey)
    )
    .map((region: IGeoRegion) => ({
      text: region.name || "",
      value: region.objectKey || "",
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const deviceRows: IServiceDeviceData[] = (deviceRowData || []) as IServiceDeviceData[];
  // Region options for DEVICE selection: only regions with at least one site group
  // (same constraint as GROUP mode — regions without site groups can't have devices).
  const deviceRegionOptions = updatedGeoRegions
    .filter((region: IGeoRegion) =>
      allowedRegionIds.includes(region.objectKey) &&
      (currentSiteGroups || []).some((group: any) => group.parentKey === region.objectKey)
    )
    .map((region: IGeoRegion) => ({
      text: region.name || "",
      value: region.name || "",
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

  // On initial render, pick a default region for the GROUP selector
  // based on the first selected region that has site groups.
  useEffect(() => {
    if (!selectedGroupRegionKey) {
      const updatedRegions: IGeoRegion[] = geoRegions || [];
      const groups = currentSiteGroups || [];
      const defaultRegion = updatedRegions
        .filter((region: IGeoRegion) => allowedRegionIds.includes(region.objectKey))
        .find((region: IGeoRegion) =>
          groups.some((group: any) => group.parentKey === region.objectKey)
        );
      if (defaultRegion && defaultRegion.objectKey) {
        setSelectedGroupRegionKey(defaultRegion.objectKey);
      }
    }
  }, [selectedGroupRegionKey, currentSiteGroups, geoRegions, allowedRegionIds]);

  useEffect(() => {
    if (selectionType === "DEVICE") {
      if (scheduledtasks?.deviceName && scheduledtasks.deviceName.length && deviceRows.length) {
        const firstDeviceKey = scheduledtasks.deviceName[0].value;
        const matchingDevice = deviceRows.find((device: IServiceDeviceData) => device.objectKey === firstDeviceKey);
        if (matchingDevice && matchingDevice.geoRegionName) {
          setSelectedDeviceRegionName(matchingDevice.geoRegionName);
          return;
        }
      }

      if (!selectedDeviceRegionName && deviceRegionOptions.length > 0) {
        setSelectedDeviceRegionName(deviceRegionOptions[0].value as string);
      }
    }
  }, [selectionType, scheduledtasks?.deviceName, deviceRows, deviceRegionOptions, selectedDeviceRegionName]);

  const filteredSiteGroups = (currentSiteGroups || []).filter(
    (group: any) => !selectedGroupRegionKey || group.parentKey === selectedGroupRegionKey
  );

  const groupList = getAllSiteGroupList(filteredSiteGroups) || [];
  const newGroupList = getNewDeviceList(groupList, scheduledtasks?.siteGroupList);
  const regionList: IOption[] = (selectedGeoRegions || [])
    .filter((region) =>
      (currentSiteGroups || []).some((group: any) => group.parentKey === region.objectKey)
    )
    .map((region) => ({
      title: region.name || "",
      value: region.objectKey || "",
    }))
    .filter((option) => option.title && option.value);
  const newRegionList = getNewDeviceList(regionList, scheduledtasks?.regionList as IOption[] || []);
  const isEditTask = useSelector(isEditTaskSelector);
  const noRegionsWithSiteGroupsMessage = "No regions with site groups are available.";
  // const [selectionType, setSelectionType] = useState("DEVICE");

  const filteredDevices = (deviceRows || []).filter(
    (device: IServiceDeviceData) => !selectedDeviceRegionName || device.geoRegionName === selectedDeviceRegionName
  );

  const deviceOptions = filteredDevices.map((item: IServiceDeviceData) => ({
    title: item.name || item.ipAddress,
    value: item.objectKey,
  }));

  const newDeviceList = getNewDeviceList(deviceOptions, scheduledtasks?.deviceName);

  const autocompleteSelectHandler = (event: any, newValue: any | null) => {
    if (selectionType === "DEVICE") {
      dispatch(setScheduledTask({ deviceName: newValue }));
    }
    else if (selectionType === "GROUP") {
      dispatch(setScheduledTask({ siteGroupList: newValue }));
    }
    else if (selectionType === "REGION") {
      dispatch(setScheduledTask({ regionList: newValue }));
    }
  };

  const clickHandler = () => {
    dispatch(setSelectedDeviceSiteGroup(constStr.ungrouped));
    navigate("/nodes", { state: constStr.ungrouped });
  };

  const onChangeHandler = (event: any) => {
    const { value } = event.target;
    if (!!value) {
      dispatch(setSelectedTaskType(value));
    }
  };

  const onGroupRegionChange = (event: any) => {
    const { value } = event.target;
    setSelectedGroupRegionKey(value || "");
    // Clear any previously selected site groups when region changes
    dispatch(setScheduledTask({ siteGroupList: [] }));
  };

  const onDeviceRegionChange = (event: any) => {
    const { value } = event.target;
    setSelectedDeviceRegionName(value || "");
    // Clear previously selected devices when region changes
    dispatch(setScheduledTask({ deviceName: [] }));
  };

  const noOption = (
    <span onClick={clickHandler} className="text-sm">
      {`No device available. `}
      <Link className="nooption-link text-primary underline" to="#">
        "Click here"
      </Link>
      {` to add devices to site group.`}
    </span>
  );

  const deviceRegionSelectOptions = deviceRegionOptions.map((o: any) => ({
    value: o.value,
    label: o.text,
  }));
  const groupRegionSelectOptions = groupRegionOptions.map((o: any) => ({
    value: o.value,
    label: o.text,
  }));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        {[
          { value: "DEVICE", label: "Device" },
          { value: "GROUP", label: "Devices in these groups" },
          { value: "REGION", label: "Devices in these regions" },
        ].map((opt) => (
          <label key={opt.value} className={`flex items-center gap-1.5 cursor-pointer text-sm ${isEditTask && selectionType !== opt.value ? "opacity-50 pointer-events-none" : ""}`}>
            <input
              type="radio"
              name="scheduledTaskRadioGroup"
              value={opt.value}
              checked={selectionType === opt.value}
              onChange={onChangeHandler}
              disabled={isEditTask && selectionType !== opt.value}
              style={{ accentColor: "var(--primary)" }}
            />
            {opt.label}
          </label>
        ))}
      </div>
      <div>
        {selectionType === "DEVICE" ? (
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm mb-3">Region</Label>
              <RadixSelectControl
                value={selectedDeviceRegionName || "__placeholder__"}
                onValueChange={(val) => onDeviceRegionChange({ target: { value: val === "__placeholder__" ? "" : val } } as any)}
                options={[{ value: "__placeholder__", label: "Region" }, ...deviceRegionSelectOptions]}
                placeholder="Region"
              />
              {deviceRegionOptions.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">{noRegionsWithSiteGroupsMessage}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="mb-3">Select Devices</Label>
              <AutocompleteSelect
                name="deviceName"
                disabled={["NodeDmBackupTask", "NodeDbBackupTask"]?.includes(scheduledtasks?.name)}
                value={scheduledtasks?.deviceName}
                multiple={true}
                id={"selectDevice"}
                options={newDeviceList}
                label={""}
                margin={"normal"}
                size={"small"}
                noOptionsText={noOption}
                onChange={autocompleteSelectHandler}
                disablePortal
                placeholder="Select..."
              />
            </div>
          </div>
        ) : selectionType === "GROUP" ? (
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-sm mb-3">Region</Label>
              <RadixSelectControl
                value={selectedGroupRegionKey || "__placeholder__"}
                onValueChange={(val) => onGroupRegionChange({ target: { value: val === "__placeholder__" ? "" : val } } as any)}
                options={[{ value: "__placeholder__", label: "Region" }, ...groupRegionSelectOptions]}
                placeholder="Region"
              />
              {groupRegionOptions.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">{noRegionsWithSiteGroupsMessage}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="mb-3">Select groups</Label>
              <AutocompleteSelect
                name="siteGroupName"
                value={scheduledtasks?.siteGroupList}
                multiple={true}
                id={"SiteGroups"}
                options={newGroupList}
                label={""}
                margin={"normal"}
                size={"small"}
                noOptionsText={noOption}
                onChange={autocompleteSelectHandler}
                disablePortal
              />
            </div>
          </div>
        ) : selectionType === "REGION" ? (
          <div className="space-y-2">
            <Label className="mb-3">Select regions</Label>
            <AutocompleteSelect
              name="regionList"
              value={scheduledtasks?.regionList}
              multiple={true}
              id={"Regions"}
              options={newRegionList}
              label={""}
              margin={"normal"}
              size={"small"}
              noOptionsText={"No regions available"}
              onChange={autocompleteSelectHandler}
              disablePortal
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const TaskDetails = () => {
  const dispatch = useStoreDispatch();

  const { dependencyTaskList } = useTaskDisplayData();

  const scheduledtasks = useSelector(tasksSelector);
  const errorMsg = useSelector(errorMsgSelector);
  const isEditTask = useSelector(isEditTaskSelector);

  const onChangeHandler = (event: any) => {
    const { name, value } = event.target;
    dispatch(setScheduledTask({ [name]: value }));
    dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
  };
  const startAtHandler = (dateTime: Dayjs | null | undefined) => {
    dispatch(setScheduledTask({ nextTime: dayjs(dateTime).valueOf() }));
  };
  const onBlurHandler = (event: any) => {
    const { name, value } = event.target;
    if (name === "name" && dependencyTaskList.length > 0) {
      const isValid = dependencyTaskList.some((item) => item.toLocaleLowerCase().trim() === value.toLocaleLowerCase().trim());
      isValid && dispatch(setErrorMsg({ ...errorMsg, [name]: "A task by that name already exists" }));
    }
  };
  const handleOnErrorDateTimePicker = (error: DateTimeValidationError, value: dayjs.Dayjs | null) => {
    if (error && error.toLowerCase() === "mintime") {
      dispatch(setErrorMsg({ ...errorMsg, startAt: `Start date and time must be greater than the current date and time.` }));
    } else {
      dispatch(setErrorMsg({ ...errorMsg, startAt: "" }));
    }
  };

  const dependencySelectOptions = dependencyTaskList.map((task: string) => ({
    value: task,
    label: task,
  }));

  const repeatSelectOptions = repeatOptions.map((opt: any) => ({
    value: opt.value,
    label: opt.text,
  }));

  const startAt = !!scheduledtasks?.nextTime ? dayjs(scheduledtasks?.nextTime) : dayjs().add(1, "minute");
  const isCustomAuditTask = scheduledtasks?.taskName?.value === "customAuditReport";
  const isCustomAuditReportType =
    scheduledtasks?.auditReportType === auditReportTypeValues.customAuditReport ||
    scheduledtasks?.auditReportType === auditReportTypeValues.customAuditReportLabel;
  const shouldHideSchedulingControls =
    (isCustomAuditTask || isCustomAuditReportType) &&
    (scheduledtasks?.customReportInputMode || "TEMPLATE") === "MANUAL";
  return (
    <div className="flex flex-col gap-5">
      {/* <p className="text-sm font-medium" style={{ color: "var(--warning)" }}>
        {"Fill in the following task related details"}
      </p> */}
      {/* Name */}
      <div className="space-y-3">
        <Label htmlFor="task-name" className="mb-3">Name</Label>
        <Input
          id="task-name"
          name="name"
          value={scheduledtasks?.name || ""}
          onChange={(e) => onChangeHandler(e)}
          onBlur={onBlurHandler}
          disabled={isEditTask}
          className={errorMsg?.name ? "border-destructive" : ""}
          placeholder="Enter name"
        />
        {errorMsg?.name && <p className="text-xs" style={{ color: "var(--destructive)" }}>{errorMsg.name}</p>}
      </div>

      {/* Description */}
      <div className="space-y-3">
        <Label htmlFor="task-description" className="mb-3">Description</Label>
        <textarea
          id="task-description"
          name="description"
          value={scheduledtasks?.description || ""}
          onChange={(e) => onChangeHandler(e)}
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent text-foreground px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          placeholder="Enter description"
        />
      </div>

      {!isCustomAuditTask && (
        <div className="space-y-3">
          <Label className="mb-3">Dependency Task</Label>
          <RadixSelectControl
            value={!scheduledtasks?.parentTask || scheduledtasks.parentTask === "None" ? "__placeholder__" : scheduledtasks.parentTask}
            onValueChange={(val) => onChangeHandler({ target: { name: "parentTask", value: val === "__placeholder__" ? "None" : val } } as any)}
            options={[{ value: "__placeholder__", label: "Dependency Task" }, ...dependencySelectOptions]}
            placeholder="Dependency Task"
          />
        </div>
      )}

      {!shouldHideSchedulingControls && (
        <>
          {/* Start At */}
          <div className="space-y-3">
            <Label className="mb-3">Start At</Label>
            <CustomDateTimePicker
              minDateTime={dayjs()}
              label=""
              value={startAt}
              handleOnChange={startAtHandler}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: "6px",
                  backgroundColor: "var(--card)",
                  border: "1px solid var(--input)",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  fontSize: 14,
                  color: "var(--foreground)",
                  "&:hover": { borderColor: "var(--muted-foreground)" },
                  "&.Mui-focused": { borderColor: "var(--ring)", boxShadow: "0 0 0 1px var(--ring)" },
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  padding: "0 12px",
                  fontSize: 14,
                  color: "var(--foreground)",
                  height: "100%",
                },
                "& .MuiInputLabel-root": { display: "none" },
              }}
              onError={handleOnErrorDateTimePicker}
            />
            {errorMsg?.startAt && (
              <p className="text-xs pl-1" style={{ color: "var(--destructive)" }}>
                {errorMsg?.startAt}
              </p>
            )}
          </div>

          {/* Repeats */}
          <div className="space-y-3">
            <Label className="mb-3">Repeats</Label>
            <RadixSelectControl
              value={scheduledtasks?.period || "__placeholder__"}
              onValueChange={(val) => onChangeHandler({ target: { name: "period", value: val === "__placeholder__" ? "" : val } } as any)}
              options={[{ value: "__placeholder__", label: "Repeats" }, ...repeatSelectOptions]}
              placeholder="Repeats"
            />
          </div>
        </>
      )}
    </div>
  );
};
